import { format } from 'date-fns'
import { jsPDF } from 'jspdf'

import { buildClinicalDashboardSummary } from './clinical-insights'
import {
  formatLabValue,
  LAB_DISEASE_PROFILES,
  matchesLabDiseaseProfile
} from './lab-results'
import {
  formatMedicalDocumentDate,
  getMedicalDocumentClinicalDate,
  getMedicalDocumentDoctor,
  getMedicalDocumentDisplayTitle,
  getMedicalDocumentSummary,
  inferMedicalDocumentSpecialty
} from './medical-document-presentation'
import type {
  ActiveMedication,
  HealthEntry,
  LabResult,
  MedicalDocument,
  MedicationHistory
} from '@/types/health'

type MedicationHistoryWithMedication = MedicationHistory & {
  medication?: {
    name?: string | null
  } | null
}

type ConsultationTimelineKind = 'document' | 'journal' | 'treatment'

interface SpecialtyFocus {
  label: string
  matchers: RegExp[]
  profileIds: string[]
  categories?: string[]
  questions: string[]
}

export interface ConsultationSummaryDocument {
  id: string
  title: string
  subtitle: string
  summary: string
  date: string
  doctor: string | null
}

export interface ConsultationSummaryLabItem {
  id: string
  document_id: string | null
  test_name: string
  date: string
  value_label: string
  abnormal_flag: string | null
  unit_warning: string | null
  source_document_title: string | null
}

export interface ConsultationTimelineItem {
  id: string
  kind: ConsultationTimelineKind
  date: string
  label: string
  detail: string
}

export interface ConsultationSpecialtySummary {
  specialty: string
  focus_label: string | null
  headline: string
  quick_summary: string
  key_points: string[]
  abnormal_biology: ConsultationSummaryLabItem[]
  documents: ConsultationSummaryDocument[]
  active_treatments: string[]
  timeline: ConsultationTimelineItem[]
  questions: string[]
}

const SPECIALTY_FOCUSES: SpecialtyFocus[] = [
  {
    label: 'Biologie',
    matchers: [/biolog/, /laborato/, /analyse/],
    profileIds: ['rch', 'endometriose', 'anemie-fer', 'thyroide', 'nutrition', 'nfs'],
    questions: [
      'Quelles anomalies sont prioritaires a recontroler ?',
      'Quel rythme de surveillance est le plus utile ?',
      'Y a-t-il une carence, une inflammation ou une anomalie hematologique a confirmer ?'
    ]
  },
  {
    label: 'Gastroenterologie',
    matchers: [/gastro/, /digest/, /colon/, /intestin/, /\brch\b/],
    profileIds: ['rch', 'nutrition', 'anemie-fer'],
    categories: ['Inflammation', 'Fer', 'Hematologie', 'Vitamines', 'Metabolisme'],
    questions: [
      'Les anomalies recentes vont-elles avec une poussee digestive ?',
      'Faut-il completer par calprotectine, CRP ou bilan de carences ?',
      'Quel signal doit faire consulter plus vite ?'
    ]
  },
  {
    label: 'Gynecologie',
    matchers: [/gyne/, /endometr/, /pelvien/, /fertil/, /uter/],
    profileIds: ['endometriose', 'anemie-fer', 'nutrition'],
    categories: ['Hormones', 'Fer', 'Hematologie', 'Vitamines', 'Inflammation'],
    questions: [
      'Les bilans orientent-ils vers un retentissement gyneco ou hormonal ?',
      'Faut-il suivre le fer ou les saignements de plus pres ?',
      'Quels examens seraient utiles au prochain rendez-vous ?'
    ]
  },
  {
    label: 'Endocrinologie',
    matchers: [/endocr/, /thyro/, /hormon/, /diabet/],
    profileIds: ['thyroide', 'metabolisme'],
    categories: ['Thyroide', 'Metabolisme', 'Hormones'],
    questions: [
      'Les symptomes et la biologie sont-ils coherents avec un desequilibre hormonal ?',
      'Quel controle thyroidien ou metabolique faut-il privilegier ?'
    ]
  },
  {
    label: 'Cardiologie',
    matchers: [/cardio/, /coeur/, /vascu/],
    profileIds: ['metabolisme'],
    categories: ['Lipides', 'Inflammation'],
    questions: [
      'Le profil lipidique ou inflammatoire appelle-t-il une surveillance particuliere ?',
      'Quel bilan complementaire serait utile avant le prochain controle ?'
    ]
  },
  {
    label: 'Hematologie',
    matchers: [/hemato/, /anemi/, /fer/],
    profileIds: ['nfs', 'anemie-fer'],
    categories: ['Fer', 'Hematologie'],
    questions: [
      'Parle-t-on plutot de carence martiale, d anemie ou d inflammation ?',
      'Faut-il suivre NFS, ferritine ou transferrine a court terme ?'
    ]
  }
]

function normalizeText(value: string | null | undefined): string {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

function uniqueItems(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.map((value) => value?.trim()).filter(Boolean) as string[])]
}

function isFiniteDate(value: string | null | undefined): value is string {
  return Boolean(value && Number.isFinite(Date.parse(value)))
}

function compareDateDesc(left: string, right: string): number {
  return Date.parse(right) - Date.parse(left)
}

function getComparableLabValue(result: LabResult): number | null {
  const normalizedValue = result.normalized_value

  if (typeof normalizedValue === 'number' && Number.isFinite(normalizedValue)) {
    return normalizedValue
  }

  return typeof result.result_value === 'number' && Number.isFinite(result.result_value)
    ? result.result_value
    : null
}

function getComparableLabUnit(result: LabResult): string | null {
  return result.normalized_unit || result.unit_group_label || result.unit || null
}

function getFocusForSpecialty(specialty: string): SpecialtyFocus | null {
  const normalizedSpecialty = normalizeText(specialty)

  return (
    SPECIALTY_FOCUSES.find((focus) =>
      focus.matchers.some((matcher) => matcher.test(normalizedSpecialty))
    ) || null
  )
}

function formatDocumentSubtitle(document: MedicalDocument): string {
  const clinicalDate = getMedicalDocumentClinicalDate(document)
  const parts = [
    `${clinicalDate.label} ${formatMedicalDocumentDate(clinicalDate.rawDate)}`,
    getMedicalDocumentDoctor(document),
    document.medical_facility
  ]

  return uniqueItems(parts).join(' - ')
}

function formatShortDate(value: string): string {
  if (!isFiniteDate(value)) {
    return value
  }

  return format(new Date(value), 'dd/MM/yyyy')
}

function describeMedication(medication: ActiveMedication): string {
  const dosagePart = medication.dosage ? ` - ${medication.dosage}` : ''
  const frequencyPart = medication.frequency ? ` - ${medication.frequency}` : ''
  return `${medication.name}${dosagePart}${frequencyPart}`
}

function buildDocumentCards(documents: MedicalDocument[]): ConsultationSummaryDocument[] {
  return documents.slice(0, 5).map((document) => ({
    id: document.id,
    title: getMedicalDocumentDisplayTitle(document),
    subtitle: formatDocumentSubtitle(document),
    summary: getMedicalDocumentSummary(document),
    date: getMedicalDocumentClinicalDate(document).rawDate,
    doctor: getMedicalDocumentDoctor(document)
  }))
}

function buildLabItems(results: LabResult[]): ConsultationSummaryLabItem[] {
  const latestByTest = new Map<string, LabResult>()

  results.forEach((result) => {
    if (!result.is_abnormal) {
      return
    }

    const key = `${result.test_name}::${result.unit_group_key || result.unit || 'no-unit'}`
    const previous = latestByTest.get(key)

    if (!previous || compareDateDesc(result.test_date, previous.test_date) < 0) {
      latestByTest.set(key, result)
    }
  })

  return [...latestByTest.values()]
    .sort((left, right) => compareDateDesc(left.test_date, right.test_date))
    .slice(0, 6)
    .map((result) => ({
      id: result.id,
      document_id: result.document_id || null,
      test_name: result.test_name,
      date: result.test_date,
      value_label: formatLabValue(
        getComparableLabValue(result),
        getComparableLabUnit(result),
        result.result_text
      ),
      abnormal_flag: result.abnormal_flag || null,
      unit_warning: result.unit_warning || null,
      source_document_title: result.source_document_title || null
    }))
}

function matchRelevantLabResults(
  specialty: string,
  results: LabResult[]
): LabResult[] {
  const focus = getFocusForSpecialty(specialty)
  const normalizedCategories = new Set((focus?.categories || []).map((category) => normalizeText(category)))
  const profileSet = new Set(
    LAB_DISEASE_PROFILES.filter((profile) => focus?.profileIds.includes(profile.id))
  )

  const relevant = results.filter((result) => {
    if (profileSet.size > 0) {
      for (const profile of profileSet) {
        if (matchesLabDiseaseProfile(result, profile)) {
          return true
        }
      }
    }

    if (focus?.categories && result.test_category) {
      return normalizedCategories.has(normalizeText(result.test_category))
    }

    return focus?.label === 'Biologie'
  })

  return relevant.length > 0 ? relevant : results
}

function buildTimeline(
  documents: MedicalDocument[],
  healthEntries: HealthEntry[],
  medicationHistory: MedicationHistoryWithMedication[]
): ConsultationTimelineItem[] {
  const documentItems = documents.slice(0, 4).map((document) => {
    const clinicalDate = getMedicalDocumentClinicalDate(document)
    return {
      id: `document-${document.id}`,
      kind: 'document' as const,
      date: clinicalDate.rawDate,
      label: getMedicalDocumentDisplayTitle(document),
      detail: formatDocumentSubtitle(document) || getMedicalDocumentSummary(document)
    }
  })

  const journalItems = healthEntries
    .filter((entry) => entry.urgent_alert || entry.bleeding_present || (entry.pain_level || 0) >= 6 || entry.summary)
    .slice(0, 3)
    .map((entry) => ({
      id: `journal-${entry.id}`,
      kind: 'journal' as const,
      date: entry.entry_date,
      label: 'Journal quotidien',
      detail:
        entry.summary ||
        (entry.bleeding_present
          ? 'Saignement note'
          : entry.urgent_alert
            ? 'Alerte prioritaire'
            : typeof entry.pain_level === 'number'
              ? `Douleur ${entry.pain_level}/10`
              : 'Point clinique')
    }))

  const treatmentItems = medicationHistory
    .filter((entry) => ['started', 'paused', 'resumed', 'stopped', 'completed'].includes(entry.action))
    .slice(0, 3)
    .map((entry) => {
      const medicationName = entry.medication?.name?.trim() || 'Traitement'
      const actionLabel =
        entry.action === 'started'
          ? 'Debut'
          : entry.action === 'paused'
            ? 'Pause'
            : entry.action === 'resumed'
              ? 'Reprise'
              : entry.action === 'completed'
                ? 'Fin'
                : 'Arret'

      return {
        id: `treatment-${entry.id}`,
        kind: 'treatment' as const,
        date: entry.change_date,
        label: `${actionLabel} traitement`,
        detail: `${medicationName}${entry.reason ? ` - ${entry.reason}` : ''}`
      }
    })

  return [...documentItems, ...journalItems, ...treatmentItems]
    .filter((item) => isFiniteDate(item.date))
    .sort((left, right) => compareDateDesc(left.date, right.date))
    .slice(0, 7)
}

function buildQuestions(
  specialty: string,
  relevantLabs: LabResult[],
  clinicalQuestions: string[]
): string[] {
  const focus = getFocusForSpecialty(specialty)
  const abnormalTests = uniqueItems(
    relevantLabs.filter((result) => result.is_abnormal).map((result) => result.test_name)
  )

  return uniqueItems([
    ...(focus?.questions || []),
    abnormalTests.length > 0
      ? `Faut-il recontroler ${abnormalTests.slice(0, 3).join(', ')} ?`
      : null,
    ...clinicalQuestions
  ]).slice(0, 5)
}

function buildQuickSummary(input: {
  specialty: string
  documents: MedicalDocument[]
  abnormalLabs: ConsultationSummaryLabItem[]
  activeTreatments: string[]
}): string {
  const latestDocument = input.documents[0]
  const clinicalDate = latestDocument ? getMedicalDocumentClinicalDate(latestDocument).rawDate : null
  const parts = [
    `${input.documents.length} document${input.documents.length > 1 ? 's' : ''}`,
    clinicalDate ? `dernier le ${formatMedicalDocumentDate(clinicalDate)}` : null,
    input.abnormalLabs.length > 0
      ? `${input.abnormalLabs.length} anomalie${input.abnormalLabs.length > 1 ? 's' : ''} recente${input.abnormalLabs.length > 1 ? 's' : ''}`
      : 'pas d anomalie biologique recente evidente',
    input.activeTreatments.length > 0
      ? `${input.activeTreatments.length} traitement${input.activeTreatments.length > 1 ? 's' : ''} actif${input.activeTreatments.length > 1 ? 's' : ''}`
      : null
  ]

  return uniqueItems(parts).join(' - ')
}

export function buildConsultationSummaries(input: {
  documents: MedicalDocument[]
  labResults: LabResult[]
  healthEntries: HealthEntry[]
  activeMedications: ActiveMedication[]
  medicationHistory: MedicationHistory[]
}): ConsultationSpecialtySummary[] {
  const clinicalSummary = buildClinicalDashboardSummary({
    healthEntries: input.healthEntries,
    activeMedications: input.activeMedications,
    medicationHistory: input.medicationHistory,
    labResults: input.labResults
  })

  const documentsBySpecialty = input.documents.reduce((groups, document) => {
    const specialty = inferMedicalDocumentSpecialty(document)
    const bucket = groups.get(specialty) || []
    bucket.push(document)
    groups.set(specialty, bucket)
    return groups
  }, new Map<string, MedicalDocument[]>())

  return [...documentsBySpecialty.entries()]
    .map(([specialty, specialtyDocuments]) => {
      const documents = [...specialtyDocuments].sort((left, right) => {
        return compareDateDesc(
          getMedicalDocumentClinicalDate(left).rawDate,
          getMedicalDocumentClinicalDate(right).rawDate
        )
      })
      const relevantLabs = matchRelevantLabResults(specialty, input.labResults)
      const abnormalBiology = buildLabItems(relevantLabs)
      const activeTreatments = input.activeMedications.slice(0, 4).map(describeMedication)
      const focus = getFocusForSpecialty(specialty)
      const keyPoints = uniqueItems([
        documents[0]
          ? `Dernier document: ${getMedicalDocumentDisplayTitle(documents[0])} - ${formatDocumentSubtitle(documents[0])}`
          : null,
        abnormalBiology[0]
          ? `Biologie recente: ${abnormalBiology[0].test_name} a ${abnormalBiology[0].value_label} le ${formatShortDate(abnormalBiology[0].date)}`
          : null,
        activeTreatments[0] ? `Traitement actif: ${activeTreatments[0]}` : null,
        ...clinicalSummary.watch_items.slice(0, 2)
      ]).slice(0, 5)

      return {
        specialty,
        focus_label: focus?.label || null,
        headline: `Preparation du rendez-vous ${specialty.toLowerCase()}`,
        quick_summary: buildQuickSummary({
          specialty,
          documents,
          abnormalLabs: abnormalBiology,
          activeTreatments
        }),
        key_points: keyPoints,
        abnormal_biology: abnormalBiology,
        documents: buildDocumentCards(documents),
        active_treatments: activeTreatments,
        timeline: buildTimeline(
          documents,
          input.healthEntries,
          input.medicationHistory as MedicationHistoryWithMedication[]
        ),
        questions: buildQuestions(
          specialty,
          relevantLabs,
          clinicalSummary.hypotheses.flatMap((hypothesis) => hypothesis.appointment_questions)
        )
      } satisfies ConsultationSpecialtySummary
    })
    .sort((left, right) => {
      const leftDate = left.documents[0]?.date || ''
      const rightDate = right.documents[0]?.date || ''

      if (leftDate && rightDate && leftDate !== rightDate) {
        return compareDateDesc(leftDate, rightDate)
      }

      return left.specialty.localeCompare(right.specialty, 'fr')
    })
}

function sanitizeFileName(value: string): string {
  return normalizeText(value).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

function addPdfSection(doc: jsPDF, title: string, items: string[], startY: number): number {
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const marginX = 42
  const lineHeight = 15
  let y = startY

  const ensureSpace = (needed: number) => {
    if (y + needed <= pageHeight - 42) {
      return
    }

    doc.addPage()
    y = 48
  }

  ensureSpace(28)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(12)
  doc.text(title, marginX, y)
  y += 18

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)

  items.forEach((item) => {
    const lines = doc.splitTextToSize(`- ${item}`, pageWidth - marginX * 2)
    ensureSpace(lines.length * lineHeight + 4)
    lines.forEach((line: string) => {
      doc.text(line, marginX, y)
      y += lineHeight
    })
    y += 3
  })

  return y + 6
}

export function exportConsultationSummaryPdf(input: {
  summary: ConsultationSpecialtySummary
  patientName?: string | null
}) {
  const doc = new jsPDF({
    unit: 'pt',
    format: 'a4'
  })
  const pageWidth = doc.internal.pageSize.getWidth()
  const marginX = 42
  const now = new Date()
  let y = 54

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(18)
  doc.text('Fiche consultation', marginX, y)

  y += 24
  doc.setFontSize(12)
  doc.text(input.summary.specialty, marginX, y)

  y += 18
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  const headerLines = doc.splitTextToSize(
    [
      input.patientName ? `Patient: ${input.patientName}` : null,
      `Genere le ${format(now, 'dd/MM/yyyy HH:mm')}`,
      input.summary.quick_summary
    ]
      .filter(Boolean)
      .join(' - '),
    pageWidth - marginX * 2
  )

  headerLines.forEach((line: string) => {
    doc.text(line, marginX, y)
    y += 14
  })

  y += 8
  y = addPdfSection(doc, 'Points a retenir', input.summary.key_points, y)
  y = addPdfSection(
    doc,
    'Biologie recente',
    input.summary.abnormal_biology.map((item) =>
      [
        `${item.test_name}: ${item.value_label}`,
        `le ${formatShortDate(item.date)}`,
        item.source_document_title
      ]
        .filter(Boolean)
        .join(' - ')
    ),
    y
  )
  y = addPdfSection(
    doc,
    'Documents cles',
    input.summary.documents.map((item) =>
      [item.title, item.subtitle, item.summary].filter(Boolean).join(' - ')
    ),
    y
  )
  y = addPdfSection(doc, 'Traitements actifs', input.summary.active_treatments, y)
  y = addPdfSection(
    doc,
    'Chronologie courte',
    input.summary.timeline.map((item) => `${formatShortDate(item.date)} - ${item.label} - ${item.detail}`),
    y
  )
  addPdfSection(doc, 'Questions du rendez-vous', input.summary.questions, y)

  doc.save(
    `consultation-${sanitizeFileName(input.summary.specialty || 'medicale')}-${format(now, 'yyyy-MM-dd')}.pdf`
  )
}

function addPdfParagraph(doc: jsPDF, title: string, text: string, startY: number): number {
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const marginX = 42
  const lineHeight = 15
  let y = startY

  const ensureSpace = (needed: number) => {
    if (y + needed <= pageHeight - 42) {
      return
    }

    doc.addPage()
    y = 48
  }

  ensureSpace(28)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(12)
  doc.text(title, marginX, y)
  y += 18

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)

  const lines = doc.splitTextToSize(text, pageWidth - marginX * 2)
  ensureSpace(lines.length * lineHeight + 4)

  lines.forEach((line: string) => {
    doc.text(line, marginX, y)
    y += lineHeight
  })

  return y + 6
}

function buildGlobalLabHighlights(results: LabResult[]): string[] {
  const latestAbnormalByTest = new Map<string, LabResult>()

  results.forEach((result) => {
    if (!result.is_abnormal) {
      return
    }

    const key = `${result.test_name}::${result.unit_group_key || result.unit || 'no-unit'}`
    const existing = latestAbnormalByTest.get(key)

    if (!existing || compareDateDesc(result.test_date, existing.test_date) < 0) {
      latestAbnormalByTest.set(key, result)
    }
  })

  return [...latestAbnormalByTest.values()]
    .sort((left, right) => compareDateDesc(left.test_date, right.test_date))
    .slice(0, 18)
    .map((result) =>
      [
        result.test_name,
        formatLabValue(
          getComparableLabValue(result),
          getComparableLabUnit(result),
          result.result_text
        ),
        `le ${formatShortDate(result.test_date)}`,
        result.source_document_title
      ]
        .filter(Boolean)
        .join(' - ')
    )
}

function buildRecentDocumentHighlights(documents: MedicalDocument[]): string[] {
  return [...documents]
    .sort((left, right) =>
      compareDateDesc(
        getMedicalDocumentClinicalDate(left).rawDate,
        getMedicalDocumentClinicalDate(right).rawDate
      )
    )
    .slice(0, 14)
    .map((document) =>
      [
        getMedicalDocumentDisplayTitle(document),
        formatDocumentSubtitle(document),
        getMedicalDocumentSummary(document)
      ]
        .filter(Boolean)
        .join(' - ')
    )
}

function buildMedicationHighlights(activeMedications: ActiveMedication[]): string[] {
  return activeMedications.slice(0, 12).map(describeMedication)
}

function buildSpecialtyHighlights(summaries: ConsultationSpecialtySummary[]): string[] {
  return summaries.flatMap((summary) =>
    uniqueItems([
      `${summary.specialty}: ${summary.quick_summary}`,
      ...summary.key_points.slice(0, 2).map((item) => `${summary.specialty} - ${item}`),
      ...summary.questions.slice(0, 2).map((item) => `${summary.specialty} - ${item}`)
    ])
  )
}

export function exportDoctorDossierPdf(input: {
  patientName?: string | null
  summaries: ConsultationSpecialtySummary[]
  documents: MedicalDocument[]
  labResults: LabResult[]
  activeMedications: ActiveMedication[]
  medicationHistory: MedicationHistory[]
  healthEntries: HealthEntry[]
}) {
  const doc = new jsPDF({
    unit: 'pt',
    format: 'a4'
  })
  const pageWidth = doc.internal.pageSize.getWidth()
  const marginX = 42
  const now = new Date()
  let y = 54

  const clinicalSummary = buildClinicalDashboardSummary({
    healthEntries: input.healthEntries,
    activeMedications: input.activeMedications,
    medicationHistory: input.medicationHistory,
    labResults: input.labResults
  })

  const overview = clinicalSummary.overview || 'Synthese clinique non disponible.'
  const watchItems = clinicalSummary.watch_items
  const actionItems = clinicalSummary.action_items
  const hypotheses = clinicalSummary.hypotheses.map((hypothesis) =>
    `${hypothesis.label} (${hypothesis.strength}) - ${hypothesis.summary}`
  )
  const correlations = clinicalSummary.correlations.map((correlation) =>
    `${correlation.label} - ${correlation.detail}`
  )
  const globalLabHighlights = buildGlobalLabHighlights(input.labResults)
  const medicationHighlights = buildMedicationHighlights(input.activeMedications)
  const documentHighlights = buildRecentDocumentHighlights(input.documents)
  const specialtyHighlights = buildSpecialtyHighlights(input.summaries)

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(20)
  doc.text('Dossier medecin', marginX, y)
  y += 24

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  const headerLines = doc.splitTextToSize(
    [
      input.patientName ? `Patient: ${input.patientName}` : null,
      `Genere le ${format(now, 'dd/MM/yyyy HH:mm')}`,
      `${input.documents.length} document(s) - ${input.labResults.length} analyse(s) - ${input.activeMedications.length} traitement(s) actif(s)`
    ]
      .filter(Boolean)
      .join(' - '),
    pageWidth - marginX * 2
  )

  headerLines.forEach((line: string) => {
    doc.text(line, marginX, y)
    y += 14
  })

  y += 6
  y = addPdfParagraph(doc, 'Vue d ensemble', overview, y)

  if (watchItems.length > 0) {
    y = addPdfSection(doc, 'Points a surveiller', watchItems, y)
  }

  if (actionItems.length > 0) {
    y = addPdfSection(doc, 'Points a discuter', actionItems, y)
  }

  if (hypotheses.length > 0) {
    y = addPdfSection(doc, 'Hypotheses cliniques', hypotheses, y)
  }

  if (correlations.length > 0) {
    y = addPdfSection(doc, 'Correlations utiles', correlations, y)
  }

  if (medicationHighlights.length > 0) {
    y = addPdfSection(doc, 'Traitements actifs', medicationHighlights, y)
  }

  if (globalLabHighlights.length > 0) {
    y = addPdfSection(doc, 'Biologie recente', globalLabHighlights, y)
  }

  if (documentHighlights.length > 0) {
    y = addPdfSection(doc, 'Documents recents cles', documentHighlights, y)
  }

  if (specialtyHighlights.length > 0) {
    addPdfSection(doc, 'Focus par specialite', specialtyHighlights, y)
  }

  doc.save(`dossier-medecin-${format(now, 'yyyy-MM-dd')}.pdf`)
}
